import ChartShapeProperty from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property';
import {
    trendLineNameMap,
    trendLineTypeMap,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline/trendline.interface';
import type { Nullable } from '../../../chart-shape-property.interface';
import type {
    ITrendlineDataItem,
    ITrendlineProperty,
    RegressionType } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline/trendline.interface';
import type {
    IChartSeries,
    ITrendline,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import { IChartDataItem } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/data-item/data-item.interface';
import { SpecialGeometryType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { STTrendlineType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';

export class TrendlineProperty extends ChartShapeProperty<ITrendline> implements ITrendlineProperty {
    public data: ITrendlineDataItem[] = [];
    public type: STTrendlineType;
    public name: string;
    public series: IChartSeries;
    public isSupport: boolean;
    private subChartDataList: IChartDataItem[];

    public init(subChartDataList: IChartDataItem[], series: IChartSeries) {
        this.type = this.uiData.type ?? STTrendlineType.PBDEFAULT;
        this.series = series;
        this.subChartDataList = subChartDataList;
        this.name = this.uiData.name || `${trendLineNameMap[this.type]}(${series.name})`;
        this.isSupport = !!trendLineTypeMap[this.type];

        return this;
    }

    public updateProps(props?: Partial<ITrendline>, removeProps?: Partial<Nullable<ITrendline>> | null): void {
        // 兼容trendline为单个对象的历史导入图表
        const sdkSer = this.sdkSubchart.ser;
        const sdkTrendline = sdkSer[this.series.idx!].trendline;
        const sdkTrendlineIsArray = Array.isArray(sdkTrendline);

        const updateSer = {
            trendlines: [...new Array(this.uiData.idx), sdkTrendlineIsArray ? props : { ...this.uiData, ...props }],
        };
        const removeSer = {
            trendlines: sdkTrendlineIsArray ? [...new Array(this.uiData.idx), removeProps] : null,
        };
        const updateSeries = [...new Array(this.series.idx), updateSer];
        const removeSeries = [...new Array(this.series.idx), removeSer];
        // 为了兼容trendline为单个对象, 把原来的单对象改成数组形式， 这里要先remove再update
        transaction(() => {
            this.subChartModel.removeProps({ series: removeSeries });
            this.subChartModel.updateProps({ series: updateSeries });
        });
    }

    public removeTrendline() {
        this.updateProps(undefined, null);
    }

    public initTrendlineDataSource(dataSet: any) {
        this.data = [];
        // 过滤出该条趋势线的数据集
        const serDataSource = this.subChartDataList
            .map((item) => {
                if (item.ser.name !== this.series.name) return undefined;
                // 线性趋势线中的空值当成0来处理
                if (this.type === STTrendlineType.linear && (item.originValue === '' || item.originValue === undefined)) {
                    return ({
                        ...item,
                        // 当category是文字是，逆向出的 数据点x轴为从1开始的整数
                        categoryIndex: item.categoryIndex + 1,
                        value: 0,
                    });
                }
                // 需要过滤掉originValue为空的，防止在回归计算中结果Row为空
                if (item.originValue !== '' && item.originValue !== undefined) {
                    return {
                        ...item,
                        // 当category是文字是，逆向出的 数据点x轴为从1开始的整数
                        categoryIndex: item.categoryIndex + 1,
                    };
                }
            })
            .filter(item => !!item);
        const trendLineDataView = dataSet.createView().source(serDataSource);

        const regressionType = trendLineTypeMap[this.uiData.type!];
        if (regressionType) {
            const trendLineProps = this.props;

            const trendLineDataSet = this.getTrendlineDataSet(
                regressionType,
                trendLineDataView,
                this.series,
            );

            if (trendLineDataSet.length > 0) {
                this.data.push(...trendLineDataSet.map((item, index) => ({
                    ...item,
                    props: trendLineProps,
                    id: `trend_${this.series.idx}_${index}_${regressionType}`,
                    ser: this.series,
                    seriesId: this.series.seriesId,
                    specialGeometryType: SpecialGeometryType.trendLine,
                })));
            }
        }
    }

    protected getTrendlineDataSet(
        regressionType: RegressionType,
        trendLineDataView: any,
        ser: IChartSeries,
    ): ITrendlineDataItem[] {
        trendLineDataView.transform({
            type: 'regression',
            method: regressionType, // 回归算法类型
            fields: ['categoryIndex', 'value'], // 统计字段
            bandwidth: 1,   // 非xy散点图情况下，数据点x轴转换为数字后为整数，所以是1
            as: ['categoryIndex', 'value'], // 结果字段
            // office的公式阶数和实际显示的对不上，暂时不设置order
            // ...(trendline.type === TrendlineType.POLYNOMIAL ? { order: trendline.order} : {})
        });

        const trendLineDataSet: any[] = trendLineDataView.rows.map((row, index) => ({
            ...row,
            name: this.name,
            category: trendLineDataView.origin[index]?.category,
        }));

        return trendLineDataSet;
    }

    private get subChartModel() {
        return this.context.getSubChartModel();
    }

    private get chartModel() {
        return this.context.getChartModel();
    }

    private get sdkSubchart() {
        // @ts-ignore
        return this.chartModel.sdkData.store.subChartList[Number(this.subChartModel.id)];
    }
}

export class DoubleValueAxisTrendlineProperty extends TrendlineProperty {
    protected getTrendlineDataSet(
        regressionType: RegressionType,
        trendLineDataView: any,
        ser: IChartSeries,
    ): ITrendlineDataItem[] {
        trendLineDataView.transform({
            type: 'regression',
            method: regressionType, // 回归算法类型
            fields: ['category', 'value'], // 统计字段
            // 在双值坐标系下，如果 数据落差太大，而又bandwidth太小，会在计算出回归数据后，valuesToPredict 的bands分割预测直接爆炸
            // 详细查看@antv/dataset中 regression的 valuesToPredict部分
            // 所以这里舍弃一部分精确性，将bandWidth交给g2自己计算，防止页面崩溃
            // bandwidth: 0.1, // xy散点图、气泡图 情况下步长设置为0.1，趋势线会更精确
            as: ['category', 'value'], // 结果字段
        });

        const trendLineDataSet: any[] = trendLineDataView.rows.map((row, index) => ({
            ...row,
            name: this.name,
        }));

        return trendLineDataSet;
    }
}
